import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Alert, AlertTitle, AppBar, Box, Paper, Stack, Toolbar, Typography } from '@mui/material'

import { IPSNameLogo } from '@/assets/icons'
import { IPS_METRO_BLUE } from '@/common/constants'
import { isHttpsUrl } from '@/common/utils/url'
import { apipyRequest } from '@/modules/api/request'
import { useSignInWithOIDC } from '@/modules/authentication'
import { fbAuth } from '@/modules/authentication'
import { monitorMessage } from '@/modules/monitoring'

const OIDCLoginPage = () => {
  const { signInWithOIDCToken, loggedInUser: user } = useSignInWithOIDC(fbAuth)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (user) navigate('/')
  }, [navigate, user])

  useEffect(() => {
    const queryString = window.location.search

    const urlParams = new URLSearchParams(queryString)
    const issuerUrl = urlParams.get('iss')
    let providerId = urlParams.get('gidp_provider_id')

    if (!issuerUrl && !providerId) {
      setErrorCode('MISSING_ISS_PARAMETER')
      return
    }

    // Ensure issuerUrl is a valid HTTPS URL to mitigate potential security risks
    if (issuerUrl && !isHttpsUrl(issuerUrl)) {
      setErrorCode('INVALID_ISS_PARAMETER')
      return
    }

    const handleLogin = async () => {
      try {
        if (issuerUrl) {
          const { gidp_provider_id } = await apipyRequest(
            '/config/oidc',
            { oidc_issuer_url: issuerUrl },
            { reject: true }
          ).catch(() => {
            throw new Error('ISSUER_UNKNOWN')
          })
          providerId = gidp_provider_id
        }

        if (!providerId) {
          throw new Error('ISSUER_NOT_PROVIDED')
        }

        await signInWithOIDCToken(providerId)
      } catch (error: any) {
        const errorCode = error && error.message
        monitorMessage('Error logging in via SSO in OIDCLogin component')
        setErrorCode(errorCode)
      }
    }

    handleLogin()
  }, [signInWithOIDCToken])

  return (
    <>
      <AppBar color="inherit" position="static">
        <Toolbar>
          <Stack direction="row" alignItems="center" height="40px">
            <IPSNameLogo />
          </Stack>
        </Toolbar>
      </AppBar>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Box sx={{ maxWidth: 650, width: 600, p: '1rem' }}>
            <Paper className="login-form" sx={{ p: '1em' }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{ color: IPS_METRO_BLUE, fontWeight: 'bold' }}
              >
                Logging you in via SSO...
              </Typography>
              {errorCode && (
                <Alert severity="error" sx={{ my: '1em' }}>
                  <AlertTitle>Login Error</AlertTitle>
                  There was an error logging you in via your SSO provider. Please contact your
                  account manager to resolve this issue. ({errorCode})
                </Alert>
              )}
            </Paper>
          </Box>
        </Box>
      </div>
    </>
  )
}

export default OIDCLoginPage
